import { Link } from "react-router-dom";
import { SIGNUPS_OPEN, PUBLIC_CODE_URL } from "../lib/flags";
import { AccountLink } from "../lib/session";
import {
  ArrowIcon,
  CheckIcon,
  ClockIcon,
  CrossIcon,
  Logo,
  PlusIcon,
  StatusIcon,
} from "../lib/ui";
import type { RequestStatus } from "../lib/types";

/** The big primary button. While sign-ups are paused it shows disabled, with
 *  the reason, instead of linking anywhere. */
function ConnectButton({ onBand = false }: { onBand?: boolean }) {
  const cls = "btn" + (onBand ? " btn--onband" : "");
  if (SIGNUPS_OPEN) {
    return (
      <Link to="/onboarding" className={cls}>
        Connect your organisation
        <ArrowIcon />
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      disabled
      title="New sign-ups are paused while we finish testing"
      style={onBand ? { background: "transparent", color: "var(--band-mute)", borderColor: "var(--band-line)" } : undefined}
    >
      Connect your organisation
    </button>
  );
}

function PausedNote({ onBand = false }: { onBand?: boolean }) {
  if (SIGNUPS_OPEN) return null;
  return (
    <p style={{ margin: "14px 0 0", fontSize: 14.5, color: onBand ? "var(--band-mute)" : "var(--ink3)" }}>
      New sign-ups are paused while we finish testing.
    </p>
  );
}

interface RowProps {
  status: RequestStatus;
  title: string;
  sub: string;
  amount: string;
  chip: string;
  dim?: boolean;
  tight?: boolean;
}

function LedgerRow({ status, title, sub, amount, chip, dim, tight }: RowProps) {
  const chipCls = status === "confirmed" ? "paid" : status === "pending" ? "wait" : "exp";
  return (
    <div className={"lrow" + (tight ? " lrow--tight" : "")}>
      <StatusIcon status={status} />
      <div style={{ minWidth: 0 }}>
        <div className="lt" style={dim ? { color: "var(--ink3)" } : undefined}>
          {title}
        </div>
        <div className="ls mono">{sub}</div>
      </div>
      <div className="mono la" style={dim ? { color: "var(--ink3)" } : undefined}>
        {amount}
      </div>
      <span className="chipcell">
        <span className={`chip chip--${chipCls}`}>{chip}</span>
      </span>
    </div>
  );
}

export default function Landing() {
  return (
    <div id="top">
      {/* Header */}
      <header className="wrap site-header">
        <Logo height={28} to={null} />
        <nav className="site-nav" aria-label="Main">
          <Link to="#how">How it works</Link>
          <Link to="#trust">Is my money safe?</Link>
          <Link to="#faq">Questions</Link>
        </nav>
        <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
          <AccountLink className="plain-link" style={{ fontSize: 15, fontWeight: 600 }}>
            Sign in
          </AccountLink>
          {SIGNUPS_OPEN && (
            <Link to="/onboarding" className="btn btn--sm hide-sm">
              Connect your org
            </Link>
          )}
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="wrap" style={{ paddingTop: 56, paddingBottom: 104 }}>
          <div className="hero-grid">
            <div>
              <h1 className="display">
                Get paid by PayNow.
                <br />
                <span className="mark-hl">Know the moment it lands.</span>
              </h1>
              <p className="lead" style={{ marginTop: 28, maxWidth: 480 }}>
                Free PayNow payment links for Singapore nonprofits and small businesses.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px 28px", marginTop: 36 }}>
                <ConnectButton />
                <Link
                  to="#how"
                  style={{ fontWeight: 600, fontSize: 16, textDecoration: "underline", textUnderlineOffset: 5, textDecorationThickness: 2 }}
                >
                  See how it works
                </Link>
              </div>
              <PausedNote />
              <p style={{ margin: "36px 0 0", display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 600 }}>
                <span style={{ color: "var(--sage-ink)", display: "inline-flex" }}>
                  <CheckIcon size={18} />
                </span>
                Your money goes bank to bank. We never hold it.
              </p>
            </div>

            {/* the product, in motion: one request flips from waiting to paid */}
            <div className="live" style={{ position: "relative", paddingBottom: 36 }}>
              <div className="ledger-card">
                <div className="ledger-card__head">
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Food Pantry</span>
                  <span className="mono" style={{ fontSize: 12, color: "var(--ink3)" }}>
                    confirmpay.link/foodpantry
                  </span>
                </div>
                <LedgerRow status="confirmed" title="Monthly donation" sub="FOOD-021 · Sofia N." amount="S$20.00" chip="Paid 14:02" />
                <LedgerRow status="confirmed" title="Volunteer shirts" sub="FOOD-020 · Daniel T." amount="S$45.00" chip="Paid 13:47" />
                <div className="lrow lrow-live">
                  <span className="stack">
                    <span className="ico ico--wait st-wait">
                      <ClockIcon size={15} />
                    </span>
                    <span className="ico ico--paid st-paid">
                      <CheckIcon size={15} />
                    </span>
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="lt">Pantry sponsorship</div>
                    <div className="ls mono">FOOD-022 · Nurul H.</div>
                  </div>
                  <div className="mono la">S$200.00</div>
                  <span className="chipcell stack">
                    <span className="chip chip--wait st-wait">Waiting</span>
                    <span className="chip chip--paid st-paid">Paid 14:09</span>
                  </span>
                </div>
                <LedgerRow status="pending" title="Monthly donation" sub="FOOD-019 · Marcus L." amount="S$20.00" chip="Waiting" />
                <LedgerRow status="expired" title="Raffle ticket" sub="FOOD-017 · Priya S." amount="S$10.00" chip="Expired" dim />
              </div>
              <div className="toast" role="status">
                <span style={{ color: "var(--sage)", display: "inline-flex" }}>
                  <CheckIcon size={18} />
                </span>
                <span>
                  Bank email matched to <span className="mono" style={{ fontWeight: 600 }}>FOOD-022</span>. Receipt sent to Nurul.
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* The problem */}
        <section className="rule-top">
          <div className="wrap sec">
            <div style={{ maxWidth: 760 }}>
              <h2 className="h2">PayNow is instant. Knowing who paid isn&rsquo;t.</h2>
              <p className="lead" style={{ marginTop: 22 }}>
                Your bank says money arrived. Not what it was for.
              </p>
            </div>

            <div className="g2" style={{ marginTop: 64, alignItems: "stretch" }}>
              <div className="compare-card compare-card--before">
                <div className="h3">Without ConfirmPay</div>
                <p className="body" style={{ marginTop: 6 }}>A stream of anonymous notifications.</p>
                <div style={{ marginTop: 22, display: "grid", gap: 10 }}>
                  <div className="notif mono"><span>PayNow received from A RAHMAN</span><span style={{ fontWeight: 600 }}>S$20.00</span></div>
                  <div className="notif mono"><span>PayNow received from NURUL H</span><span style={{ fontWeight: 600 }}>S$20.00</span></div>
                  <div className="notif mono"><span>PayNow received from D TAN</span><span style={{ fontWeight: 600 }}>S$45.00</span></div>
                </div>
                <ul className="ticks" style={{ marginTop: 26 }}>
                  <li><span aria-hidden="true" style={{ color: "var(--red-ink)", display: "inline-flex", marginTop: 3 }}><CrossIcon size={16} /></span>Which donation was this for?</li>
                  <li><span aria-hidden="true" style={{ color: "var(--red-ink)", display: "inline-flex", marginTop: 3 }}><CrossIcon size={16} /></span>Did anyone pay twice, or not at all?</li>
                  <li><span aria-hidden="true" style={{ color: "var(--red-ink)", display: "inline-flex", marginTop: 3 }}><CrossIcon size={16} /></span>Someone scrolls the banking app and ticks a spreadsheet.</li>
                </ul>
              </div>

              <div className="compare-card compare-card--after">
                <div className="h3">With ConfirmPay</div>
                <p className="body" style={{ marginTop: 6 }}>Every payment has its own reference.</p>
                <div style={{ marginTop: 22, border: "1px solid var(--line)", borderRadius: 6, overflow: "hidden" }}>
                  <LedgerRow tight status="confirmed" title="Monthly donation" sub="FOOD-021 · Sofia N." amount="S$20.00" chip="Paid" />
                  <LedgerRow tight status="confirmed" title="Monthly donation" sub="FOOD-023 · Nurul H." amount="S$20.00" chip="Paid" />
                  <LedgerRow tight status="confirmed" title="Volunteer shirts" sub="FOOD-020 · Daniel T." amount="S$45.00" chip="Paid" />
                </div>
                <ul className="ticks" style={{ marginTop: 26 }}>
                  <li><span style={{ color: "var(--sage-ink)", display: "inline-flex", marginTop: 3 }}><CheckIcon size={18} /></span>Each payment is matched to the request it paid.</li>
                  <li><span style={{ color: "var(--sage-ink)", display: "inline-flex", marginTop: 3 }}><CheckIcon size={18} /></span>Payers get their receipt without you lifting a finger.</li>
                  <li><span style={{ color: "var(--sage-ink)", display: "inline-flex", marginTop: 3 }}><CheckIcon size={18} /></span>You see who is paid, who is waiting, and what has expired.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how" className="rule-top">
          <div className="wrap sec">
            <h2 className="h2" style={{ maxWidth: 720 }}>Share a link. They pay. You both see it.</h2>

            <div className="g3" style={{ marginTop: 64, alignItems: "stretch" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div className="step-label">STEP 1</div>
                <div className="step-visual">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, border: "1.5px solid var(--ink)", borderRadius: 6, padding: "12px 14px" }}>
                    <span className="mono" style={{ fontSize: 13, fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>confirmpay.link/foodpantry</span>
                    <span style={{ flex: "none", fontSize: 12, fontWeight: 700, background: "var(--btn-bg)", color: "var(--btn-fg)", borderRadius: 4, padding: "5px 10px" }}>Copy</span>
                  </div>
                  <div style={{ textAlign: "center", fontSize: 13, color: "var(--ink3)" }}>or create a request</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, borderBottom: "1px solid var(--line)", paddingBottom: 8 }}><span style={{ color: "var(--ink2)" }}>Amount</span><span className="mono" style={{ fontWeight: 600 }}>S$20.00</span></div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, borderBottom: "1px solid var(--line)", paddingBottom: 8 }}><span style={{ color: "var(--ink2)" }}>For</span><span style={{ fontWeight: 600 }}>Monthly donation</span></div>
                  </div>
                </div>
                <h3 className="h3">Send a link</h3>
                <p className="body">Create a request for an exact amount, or share your one permanent link and let supporters enter their own.</p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div className="step-label">STEP 2</div>
                <div className="step-visual" style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 22 }}>
                  <MiniQr />
                  <div style={{ display: "grid", gap: 10 }}>
                    <div className="mono" style={{ fontSize: 24, fontWeight: 600 }}>S$20.00</div>
                    <div className="mono" style={{ fontSize: 13, color: "var(--ink2)" }}>Ref FOOD-021</div>
                    <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.4 }}>Pays your own<br />PayNow number</div>
                  </div>
                </div>
                <h3 className="h3">They scan and pay</h3>
                <p className="body">The PayNow QR carries your own number or UEN, the exact amount and a unique reference. The money goes bank to bank.</p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div className="step-label">STEP 3</div>
                <div className="step-visual">
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span className="ico ico--paid" style={{ width: 40, height: 40 }}><CheckIcon size={20} /></span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 17 }}>Payment received</div>
                      <div className="mono" style={{ fontSize: 12.5, color: "var(--ink3)" }}>FOOD-021 · 14:02</div>
                    </div>
                  </div>
                  <div style={{ borderTop: "1px solid var(--line)", paddingTop: 14, display: "grid", gap: 8, fontSize: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--ink2)" }}>Receipt emailed to</span><span style={{ fontWeight: 600 }}>Sofia N.</span></div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><span style={{ color: "var(--ink2)" }}>Status in your dashboard</span><span className="chip chip--paid">Paid</span></div>
                  </div>
                </div>
                <h3 className="h3">You both get confirmation</h3>
                <p className="body">When your bank emails you about the payment, ConfirmPay matches the reference, marks the request paid, and emails the payer a receipt.</p>
              </div>
            </div>

            <div className="setup-card" style={{ marginTop: 80 }}>
              <div className="g2 g2--setup">
                <div>
                  <div className="h3" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>One-time setup, about five minutes</div>
                  <p className="body" style={{ marginTop: 12 }}>No app, no code, no password to hand over.</p>
                </div>
                <div>
                  <ol style={{ listStyle: "none", padding: 0, display: "grid", gap: 18, fontSize: 17 }}>
                    <li style={{ display: "flex", gap: 16 }}><span className="mono" style={{ fontWeight: 600, color: "var(--ink3)", flex: "none", width: 22 }}>1</span><span><strong>Add your PayNow number or UEN.</strong> That is where every payment will go.</span></li>
                    <li style={{ display: "flex", gap: 16 }}><span className="mono" style={{ fontWeight: 600, color: "var(--ink3)", flex: "none", width: 22 }}>2</span><span><strong>Check your bank emails you.</strong> ConfirmPay reads the payment emails your bank already sends, so tick off that they arrive.</span></li>
                    <li style={{ display: "flex", gap: 16 }}><span className="mono" style={{ fontWeight: 600, color: "var(--ink3)", flex: "none", width: 22 }}>3</span><span><strong>Forward your bank&rsquo;s payment emails.</strong> One filter in Gmail or Outlook sends a copy to a private address we give you.</span></li>
                    <li style={{ display: "flex", gap: 16 }}><span className="mono" style={{ fontWeight: 600, color: "var(--ink3)", flex: "none", width: 22 }}>4</span><span><strong>Send a test email.</strong> When it arrives, you are live.</span></li>
                  </ol>
                  <p style={{ margin: "26px 0 0", fontSize: 14.5, lineHeight: 1.6, color: "var(--ink2)", borderTop: "1px solid var(--line)", paddingTop: 18 }}>
                    Reads payment notifications from DBS/POSB, OCBC, UOB, Standard Chartered, Maybank, HSBC, Citibank, CIMB, Trust Bank and GXS Bank. Bank not on the list? Forward its emails anyway and mark payments paid by hand.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* One link for everyone */}
        <section className="band">
          <div className="wrap sec">
            <div className="hero-grid hero-grid--band">
              <div>
                <h2 className="h2" style={{ color: "var(--band-fg)" }}>One link for everyone you collect from.</h2>
                <p className="lead" style={{ marginTop: 22, color: "var(--band-mute)", maxWidth: 520 }}>
                  Put <span className="mono" style={{ color: "var(--band-fg)", fontSize: 17 }}>confirmpay.link/yourname</span> on a poster or in a group chat. Supporters enter their own amount and pay.
                </p>
                <ul style={{ listStyle: "none", margin: "36px 0 0", padding: 0, display: "grid", gap: 14, fontSize: 17 }}>
                  {[
                    "Stays the same for good, so printed posters never go stale.",
                    "Switch it off any time, for example when a campaign ends.",
                    "Change the name later and the old link keeps working.",
                  ].map((t) => (
                    <li key={t} style={{ display: "flex", gap: 12 }}>
                      <span style={{ color: "var(--sage)", display: "inline-flex", marginTop: 3 }}><CheckIcon size={20} /></span>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>
                <div className="phone" aria-hidden="true">
                  <div className="phone__screen">
                    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".02em", color: "var(--ink3)" }}>ConfirmPay</div>
                    <div style={{ fontSize: 25, fontWeight: 800, letterSpacing: "-0.035em", lineHeight: 1.05, marginTop: 10 }}>Pay Food Pantry</div>
                    <div style={{ marginTop: 20, display: "grid", gap: 12 }}>
                      <div><div style={{ fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Amount</div><div className="mono" style={{ border: "1.5px solid var(--ink)", borderRadius: 6, padding: "10px 12px", fontSize: 20, fontWeight: 600, background: "var(--raised)" }}>S$ 20.00</div></div>
                      <div><div style={{ fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Your name</div><div style={{ border: "1px solid var(--edge)", borderRadius: 6, padding: "11px 12px", fontSize: 14, background: "var(--raised)", color: "var(--ink2)" }}>Sofia N.</div></div>
                      <div style={{ background: "var(--btn-bg)", color: "var(--btn-fg)", borderRadius: 6, textAlign: "center", padding: 13, fontSize: 15, fontWeight: 600, marginTop: 4 }}>Continue to payment</div>
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--ink3)", marginTop: 14, textAlign: "center" }}>Pays Food Pantry directly by PayNow</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust */}
        <section id="trust" className="rule-top">
          <div className="wrap sec">
            <div style={{ maxWidth: 760 }}>
              <h2 className="h2">Your money never passes through us.</h2>
              <p className="lead" style={{ marginTop: 22 }}>
                Two separate paths. The money goes bank to bank. ConfirmPay only ever sees a copy of the email your bank sends you.
              </p>
            </div>

            <div style={{ marginTop: 56, border: "1px solid var(--line2)", borderRadius: 8, background: "var(--raised)", padding: "36px 36px 32px", display: "grid", gap: 34 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".04em", color: "var(--sage-ink)", marginBottom: 14 }}>
                  THE MONEY &middot; NEVER TOUCHES CONFIRMPAY
                </div>
                <div className="lane">
                  <div className="node">Payer&rsquo;s bank<small>Scans your PayNow QR</small></div>
                  <div className="wire wire--solid" />
                  <div className="node node--you">Your bank account<small>Your own PayNow number or UEN</small></div>
                </div>
              </div>
              <div style={{ borderTop: "1px solid var(--line)" }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".04em", color: "var(--ink2)", marginBottom: 14 }}>
                  THE PROOF &middot; A COPY OF YOUR BANK&rsquo;S EMAIL
                </div>
                <div className="lane">
                  <div className="node">Bank emails you<small>You already get this</small></div>
                  <div className="wire wire--dash" />
                  <div className="node">Your filter<small>Forwards a copy</small></div>
                  <div className="wire wire--dash" />
                  <div className="node node--cp">ConfirmPay reads it<small>Amount, reference, payer name</small></div>
                  <div className="wire wire--dash" />
                  <div className="node">Request marked paid<small>Receipt sent to the payer</small></div>
                </div>
              </div>
            </div>

            <div className="g2" style={{ marginTop: 56, gap: "0 64px" }}>
              <div className="fact"><div className="h3" style={{ fontSize: 20 }}>Non-custodial by design</div><p className="body" style={{ marginTop: 8 }}>Every QR contains your own PayNow number or UEN. There is no ConfirmPay wallet for money to sit in.</p></div>
              <div className="fact"><div className="h3" style={{ fontSize: 20 }}>Reads three things</div><p className="body" style={{ marginTop: 8 }}>The amount, the reference and the payer&rsquo;s name as your bank prints it. The rest of the email is not kept.</p></div>
              {PUBLIC_CODE_URL ? (
                <div className="fact"><div className="h3" style={{ fontSize: 20 }}>Code you can check</div><p className="body" style={{ marginTop: 8 }}>The part that reads your bank&rsquo;s email is public, so anyone can see exactly what it does.</p></div>
              ) : (
                <div className="fact"><div className="h3" style={{ fontSize: 20 }}>Switch it off yourself</div><p className="body" style={{ marginTop: 8 }}>Delete the filter in your own inbox and forwarding stops at once. You do not need to ask us.</p></div>
              )}
              <div className="fact"><div className="h3" style={{ fontSize: 20 }}>No AI in the payment path</div><p className="body" style={{ marginTop: 8 }}>Matching is plain, deterministic code. The same email always gives the same answer.</p></div>
            </div>
            <p style={{ margin: "24px 0 0" }}>
              <Link to="/trust" style={{ fontWeight: 600, fontSize: 16, textUnderlineOffset: 5, textDecorationThickness: 2 }}>
                See exactly what happens to a forwarded email
              </Link>
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="rule-top">
          <div className="wrap sec">
            <div className="g2 g2--faq">
              <h2 className="h2">Questions people ask first</h2>
              <div>
                <details className="qa" open>
                  <summary><span>What does ConfirmPay cost?</span><PlusIcon /></summary>
                  <p className="body">Nothing. It is free for Singapore nonprofits and small businesses, and there are no per-payment fees because ConfirmPay never handles the payment.</p>
                </details>
                <details className="qa">
                  <summary><span>Does ConfirmPay hold or move my money?</span><PlusIcon /></summary>
                  <p className="body">No. Payers pay your PayNow number or UEN directly. ConfirmPay never receives, holds or routes funds.</p>
                </details>
                <details className="qa">
                  <summary><span>Which banks does it work with?</span><PlusIcon /></summary>
                  <p className="body">It reads payment emails from DBS/POSB, OCBC, UOB, Standard Chartered, Maybank, HSBC, Citibank, CIMB, Trust Bank and GXS Bank. If yours is not listed, forward its emails anyway and confirm payments by hand from the dashboard.</p>
                </details>
                <details className="qa">
                  <summary><span>What if someone pays the wrong amount?</span><PlusIcon /></summary>
                  <p className="body">That is unlikely. A one-time payment link comes with the amount already filled into the PayNow QR, so your payer just confirms it in their banking app. If an amount still comes out different, your dashboard shows what you asked for next to what actually arrived, and once you have seen the money in your own bank app you can mark the request paid by hand.</p>
                </details>
                <details className="qa">
                  <summary><span>Do I need to install anything?</span><PlusIcon /></summary>
                  <p className="body">No app and no code. Setup is a PayNow number plus one email forwarding rule that lives in your own inbox, so you can delete it yourself at any time.</p>
                </details>
              </div>
            </div>
          </div>
        </section>

        {/* Final call to action */}
        <section className="band">
          <div className="wrap" style={{ padding: "104px 40px", textAlign: "center" }}>
            <h2 className="display" style={{ fontSize: "min(68px, 12vw)", color: "var(--band-fg)" }}>Stop matching payments by hand.</h2>
            <p className="lead" style={{ margin: "24px auto 0", maxWidth: 560, color: "var(--band-mute)" }}>
              Connect your organisation in about five minutes. Free, with no app to install.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 14, marginTop: 40 }}>
              <ConnectButton onBand />
              <AccountLink className="btn btn--onband-ghost" signedInLabel="Open your dashboard">Already connected? Sign in</AccountLink>
            </div>
            <PausedNote onBand />
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="wrap site-footer" style={{ maxWidth: 1240, margin: "0 auto" }}>
        <Logo height={22} to={null} />
        <nav aria-label="Footer">
          <Link to="#how">How it works</Link>
          <Link to="/trust">Trust</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/delete-data">Delete my data</Link>
          <AccountLink>Sign in</AccountLink>
        </nav>
        <p style={{ fontSize: 13.5, color: "var(--ink3)" }}>Free for Singapore nonprofits and small businesses.</p>
      </footer>
    </div>
  );
}

/** A small decorative QR for the "they scan and pay" step (not a real code). */
function MiniQr() {
  return (
    <svg viewBox="0 0 90 90" width="116" height="116" aria-hidden="true" style={{ color: "var(--ink)", flex: "none" }}>
      <path
        fill="currentColor"
        d="M0 0h30v10H0zM40 0h10v10H40zM60 0h30v10H60zM0 10h10v10H0zM20 10h10v10H20zM50 10h20v10H50zM80 10h10v10H80zM0 20h30v10H0zM40 20h10v10H40zM60 20h30v10H60zM40 30h20v10H40zM0 40h10v10H0zM20 40h20v10H20zM60 40h10v10H60zM80 40h10v10H80zM10 50h10v10H10zM40 50h20v10H40zM70 50h10v10H70zM0 60h30v10H0zM50 60h10v10H50zM80 60h10v10H80zM0 70h10v10H0zM20 70h10v10H20zM40 70h10v10H40zM60 70h20v10H60zM0 80h30v10H0zM50 80h20v10H50zM80 80h10v10H80z"
      />
    </svg>
  );
}
